import type { CityConfig } from "../city-template/types";

export const paducahConfig: CityConfig = {
  city: "Paducah",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "paducah",
  route: "/cities/paducah",
  title: "Emergency Passport Paducah, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Paducah, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for McCracken County and Western Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Paducah, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Paducah and Western Kentucky travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Paducah%20Kentucky%20riverfront%20downtown%20at%20golden%20hour%20with%20historic%20flood%20wall%20murals%2C%20Ohio%20River%20waterfront%2C%20charming%20river%20city%20atmosphere%2C%20warm%20western%20Kentucky%20sunset%20light%2C%20National%20Quilt%20Museum%20district%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20McCracken%20County%20scene%20with%20river%20barges%20and%20scenic%20waterfront&width=1800&height=700&seq=paducah-hero-001&orientation=landscape",
  heroImageAlt: "Paducah Kentucky riverfront downtown at golden hour with historic flood wall murals and Ohio River waterfront",
  geoPosition: "37.0834;-88.6000",
  geoPlacename: "Paducah, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 3800, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 330 miles from Paducah via I-24",
  },
  nearbyCitiesZips: [
    { name: "Paducah", zips: "42001, 42002, 42003" },
    { name: "Mayfield", zips: "42066" },
    { name: "Murray", zips: "42071, 42072" },
    { name: "Benton", zips: "42025" },
    { name: "Calvert City", zips: "42029" },
    { name: "Metropolis (IL)", zips: "62960" },
    { name: "Massac (IL)", zips: "62901" },
    { name: "Grand Rivers", zips: "42045" },
    { name: "Gilbertsville", zips: "42044" },
    { name: "Kevil", zips: "42053" },
    { name: "Reidland", zips: "42003" },
    { name: "Lone Oak", zips: "42001" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent McCracken County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Louisville", route: "/cities/louisville" },
    { name: "Fast passport renewal in Murray", route: "/cities/murray" },
    { name: "Emergency passport help in Owensboro", route: "/cities/owensboro" },
    { name: "Urgent passport services in Mayfield", route: "/mayfield" },
    { name: "Passport courier services near Henderson", route: "/cities/henderson" },
    { name: "Same-day passport help in Hopkinsville", route: "/cities/hopkinsville" },
  ],
  fpcOfficeAddress: "500 W Jefferson St, Louisville, KY 40202",
  fpcOfficeNote: "Fast Passport Center serves Paducah remotely via secure courier. We do not have a walk-in office in Paducah — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for McCracken County travelers.",
  fpcOfficeDistance: "Serving all of McCracken County and Western Kentucky",
  serviceAreaIntro: "From downtown Paducah's riverfront arts district to the historic quilt district, from the surrounding farmland to the nearby Illinois border — Fast Passport Center serves the entire Paducah community. We regularly help travelers from Mayfield, Murray, and Benton get their passports fast.",
  nearbyCitiesIntro: "We serve all Paducah neighborhoods and nearby Western Kentucky cities including Mayfield, Murray, Benton, Calvert City, Metropolis (IL), Massac (IL), Grand Rivers, Gilbertsville, Kevil, Reidland, and Lone Oak. If you are in McCracken County, western Kentucky, or the southern Illinois border area, we have you covered.",
  howItWorksIntro: "Most Paducah travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Paducah travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Paducah and McCracken County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Paducah? Here are the most common ones we hear from McCracken County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  dmvWebsite: "https://drive.ky.gov",
  dmvLabel: "Kentucky Transportation Cabinet — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent McCracken County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Rebecca K.",
      location: "Paducah, KY",
      rating: 5,
      text: "My passport expired right before my quilting conference in Cairo. Fast Passport Center got me a renewal in 3 days. I made it to Egypt and didn't miss a single workshop.",
      service: "Passport Renewal",
    },
    {
      name: "James M.",
      location: "Mayfield, KY",
      rating: 5,
      text: "Emergency river cruise on the Mississippi to New Orleans, then flying to Paris. They got me an emergency passport in 36 hours. Saved my vacation and business meetings.",
      service: "Emergency Passport",
    },
    {
      name: "Linda S.",
      location: "Murray, KY",
      rating: 5,
      text: "First-time passport for my retirement cruise to the Caribbean. They helped me find the nearest acceptance facility and I had my passport in 5 days. Couldn't have been easier.",
      service: "New Passport",
    },
    {
      name: "Robert T.",
      location: "Paducah, KY",
      rating: 5,
      text: "Lost my passport before my medical emergency trip to Toronto. They got me a replacement in 24 hours. The team was compassionate and incredibly fast during a stressful time.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "paducah-1",
      name: "Paducah Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1900 Broadway St",
      city: "Paducah",
      state: "KY",
      zip: "42001",
      phone: "(270) 444-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Paducah",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for McCracken County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "paducah-2",
      name: "Mayfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "128 E Broadway",
      city: "Mayfield",
      state: "KY",
      zip: "42066",
      phone: "(270) 247-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Mayfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Graves County and western McCracken County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "paducah-3",
      name: "Murray Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "614 N 12th St",
      city: "Murray",
      state: "KY",
      zip: "42071",
      phone: "(270) 753-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Murray",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Calloway County and Murray State University travelers. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "paducah-4",
      name: "Benton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "804 Main St",
      city: "Benton",
      state: "KY",
      zip: "42025",
      phone: "(270) 527-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Benton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Marshall County and eastern McCracken County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "paducah-5",
      name: "Calvert City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "695 5th Ave",
      city: "Calvert City",
      state: "KY",
      zip: "42029",
      phone: "(270) 395-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Calvert City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Marshall County and nearby recreational areas. Call for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "paducah-6",
      name: "McCracken County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "301 S 6th St",
      city: "Paducah",
      state: "KY",
      zip: "42003",
      phone: "(270) 444-4700",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from McCracken County Courthouse",
      website: "https://www.paducahky.gov/",
      websiteLabel: "McCracken County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5.5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "McCracken County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at McCracken County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Paducah applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many McCracken County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "McCracken County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Paducah." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Paducah address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Paducah before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Paducah for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5.5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Paducah?", a: "Fast Passport Center offers fast passport renewal in Paducah in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Paducah in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Paducah?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 5.5 hour drive from Paducah — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for McCracken County residents." },
    { q: "Can I expedite a child passport in Paducah?", a: "Yes. Fast Passport Center expedites child passports in Paducah for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk McCracken County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Paducah with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Paducah?", a: "With Fast Passport Center's emergency courier service, Paducah travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Paducah?", a: "Popular options include the Paducah Post Office (42001), Mayfield Post Office (42066), Murray Post Office (42071), Benton Post Office (42025), Calvert City Post Office (42029), and McCracken County Clerk (42003). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Paducah?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 5.5 hour drive to Chicago." },
    { q: "What if I live in a smaller McCracken County town?", a: "We serve the entire McCracken County and surrounding area including Mayfield, Murray, Benton, Calvert City, Metropolis (IL), Massac (IL), Grand Rivers, Gilbertsville, Kevil, Reidland, and Lone Oak. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Paducah?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Paducah?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Paducah and Mayfield offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Paducah?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Paducah?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk McCracken County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Paducah", address: "2600 Lone Oak Rd, Paducah, KY 42003", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Paducah", address: "5135 Hinkleville Rd, Paducah, KY 42001", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Paducah", address: "5150 Hinkleville Rd, Paducah, KY 42001", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Paducah", address: "802 Broadway St, Paducah, KY 42001", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Mayfield", address: "1108 Paris Rd, Mayfield, KY 42066", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Murray", address: "809 N 12th St, Murray, KY 42071", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Murray", address: "1202 N 12th St, Murray, KY 42071", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Paducah", address: "2500 Hinkleville Rd, Paducah, KY 42001", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — McCracken County",
      address: "301 S 6th St, Paducah, KY 42003",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Graves County",
      address: "101 E South St, Mayfield, KY 42066",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Marshall County",
      address: "1101 Main St, Benton, KY 42025",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Marshall County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Calloway County",
      address: "101 S 5th St, Murray, KY 42071",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Calloway County and Murray State residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PAH",
      name: "Barkley Regional Airport",
      distance: "10 miles from downtown Paducah",
      address: "2901 Fisher Rd, West Paducah, KY 42086",
      note: "The primary airport for McCracken County. Regional flights with connections to Chicago and other major hubs.",
      flightUrl: "https://www.flypaducah.com/",
      flightLabel: "PAH Airport",
      featured: true,
    },
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "140 miles from Paducah",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Major international hub for long-haul flights to Europe, Asia, and the Caribbean. Accessible via I-24.",
      flightUrl: "https://www.flynashville.com/",
      flightLabel: "BNA Airport",
      featured: false,
    },
    {
      code: "STL",
      name: "St. Louis Lambert International",
      distance: "180 miles from Paducah",
      address: "10701 Lambert International Blvd, St. Louis, MO 63145",
      note: "Alternative regional airport for international flights. Accessible via I-24 and I-55.",
      flightUrl: "https://www.flystl.com/",
      flightLabel: "STL Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Kentucky Office of Vital Statistics in Frankfort — order certified copies online, by mail, or in person for passport applications and name changes.", url: "https://www.chfs.ky.gov/agencies/dph/dehp/vsb/Pages/default.aspx" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "McCracken County Clerk — certified marriage licenses for name change passport applications and legal documentation.", url: "https://mccrackencountyclerk.com/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "McCracken Circuit Court Clerk — certified divorce decrees supporting passport name change or correction.", url: "https://kycourts.gov/Courts/Circuit-Court/Pages/McCracken.aspx" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Kentucky, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};